/**
 * AgentSelector — 에이전트 전환 드롭다운
 *
 * 프리셋 에이전트 목록만 표시. 커스텀 에이전트 생성/목록 기능은 제거됨.
 */

import { useState, useRef, useEffect } from "react";
import { ChevronDown, Bot, Check } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { ScrollArea } from "@/shared/components";
import { useAgentDefinitions } from "@/agent/data/agents";
import type { AgentDefinition } from "@modootoday/core-agent/types";

export interface AgentSelectorProps {
  selectedAgentId: string;
  onSelect: (agentId: string) => void;
  disabled?: boolean;
  className?: string;
}

function TierBadge({ tier }: { tier: AgentDefinition["requiredTier"] }) {
  if (tier === "free") return null;
  const colors = {
    starter: "bg-blue-100 text-blue-700",
    pro: "bg-violet-100 text-violet-700",
    enterprise: "bg-amber-100 text-amber-700",
  } as const;
  return (
    <span
      className={cn(
        "rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase",
        colors[tier],
      )}
    >
      {tier}
    </span>
  );
}

export function AgentSelector({
  selectedAgentId,
  onSelect,
  disabled = false,
  className,
}: AgentSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: agents = [], isLoading } = useAgentDefinitions();

  const selectedAgent = agents.find((a) => a.id === selectedAgentId);

  // 외부 클릭 시 닫기
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  function handleSelect(agentId: string) {
    onSelect(agentId);
    setOpen(false);
  }

  // 커스텀 에이전트 기능 제거 — 프리셋(userId 없는 것)만 표시
  const presets = agents.filter((a) => !a.userId);

  return (
    <div ref={ref} className={cn("relative", className)}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled || isLoading}
        className={cn(
          "flex items-center gap-1.5 rounded-lg border border-stroke-subtle bg-surface-raised px-3 py-1.5",
          "text-[13px] font-medium text-text-primary",
          "hover:bg-surface-secondary transition-colors",
          "disabled:cursor-not-allowed disabled:opacity-60",
        )}
      >
        <Bot className="size-4 text-text-brand shrink-0" />
        <span className="max-w-[140px] truncate">
          {isLoading
            ? "불러오는 중..."
            : (selectedAgent?.name ?? "에이전트 선택")}
        </span>
        <ChevronDown
          className={cn(
            "size-3.5 text-text-tertiary transition-transform shrink-0",
            open && "rotate-180",
          )}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-xl border border-stroke-subtle bg-surface-raised shadow-lg">
          <ScrollArea className="max-h-80">
            <div className="py-1.5">
              {presets.map((agent) => (
                <AgentOption
                  key={agent.id}
                  agent={agent}
                  isSelected={agent.id === selectedAgentId}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

function AgentOption({
  agent,
  isSelected,
  onSelect,
}: {
  agent: AgentDefinition;
  isSelected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(agent.id)}
      className={cn(
        "flex w-full items-start gap-2.5 px-3 py-2 transition-colors",
        isSelected ? "bg-accent/5" : "hover:bg-surface-secondary",
      )}
    >
      <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-accent/10">
        <Bot className="size-3.5 text-text-brand" />
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-1.5">
          <span className="text-[12px] font-medium text-text-primary truncate">
            {agent.name}
          </span>
          <TierBadge tier={agent.requiredTier} />
        </div>
        {agent.description && (
          <p className="text-[11px] text-text-tertiary leading-snug line-clamp-1 mt-0.5">
            {agent.description}
          </p>
        )}
      </div>
      {isSelected && (
        <Check className="mt-1 size-3.5 shrink-0 text-text-brand" />
      )}
    </button>
  );
}
